// SKU Secrecy Utility
// For non-logged-in users and public role users, show product ID instead of SKU
// Except for Accessories (category = 'ACCESS')

/**
 * Check if user should see SKU (true) or ID (false)
 * @param {Object} user - User object from auth state
 * @param {Object} product - Product object
 * @returns {boolean} - true = show SKU, false = show ID
 */
export const shouldShowSku = (user, product) => {
  // Always show SKU for accessories (category = 'ACCESS')
  const categoryName = (product?.category?.name || product?.category || '').toString().toUpperCase();
  if (categoryName === 'ACCESS' || categoryName === 'ACCESSORIES') {
    return true;
  }

  // If no user (not logged in), hide SKU
  if (!user) {
    return false;
  }

  // Check user role
  const role = String(user?.role || '').toLowerCase();

  // Public role users should NOT see SKU
  if (role === 'public') {
    return false;
  }

  // All other authenticated users (vip, admin, staff, manager, etc.) see SKU
  return true;
};

/**
 * Get the display identifier for a product (SKU or ID based on user role)
 * @param {Object} user - User object from auth state
 * @param {Object} product - Product object
 * @returns {string} - SKU or ID to display
 */
export const getProductDisplayId = (user, product) => {
  if (!product) return '';

  if (shouldShowSku(user, product)) {
    return product.sku || '';
  }

  // Return product ID for non-SKU users
  return String(product.id || '');
};

/**
 * Get label for the identifier column (SKU or ID)
 * @param {Object} user - User object from auth state
 * @param {Object} product - Product object
 * @returns {string} - 'SKU' or 'ID'
 */
export const getIdLabel = (user, product) => {
  return shouldShowSku(user, product) ? 'SKU' : 'ID';
};

/**
 * Check if current user is public/non-logged-in (needs ID instead of SKU)
 * @param {Object} user - User object from auth state
 * @param {boolean} isAuthenticated - Authentication status
 * @returns {boolean}
 */
export const isPublicOrAnonymous = (user, isAuthenticated) => {
  if (!isAuthenticated || !user) return true;
  const role = String(user?.role || '').toLowerCase();
  return role === 'public';
};
